/**
 * One form field, drawn from the content pack's concept definition: numbers with their unit and
 * range, yes/no questions that can stay unanswered, short choice lists as buttons, long lists
 * (diagnoses) searched by typing, and free text written at length.
 */
import type { ConceptDef, FormValues, Value } from '@assist/core';
import { checkValue } from '@assist/core';
import { clock } from '../controller.js';
import { t } from '../i18n.js';
import { SearchPicker } from './SearchPicker.js';

/** What the provider has typed or picked, before parsing. */
export type Entry = string | boolean;
export type Entries = Record<string, Entry | undefined>;

export function parseEntry(c: ConceptDef, e: Entry | undefined): Value {
  if (e === undefined || e === '') return null;
  if (c.type === 'boolean') return e === true;
  if (c.type === 'quantity' || c.type === 'integer') {
    const s = String(e).trim().replace(',', '.');
    if (!/^-?\d*\.?\d+$/.test(s)) return Number.NaN;
    return Number(s);
  }
  return String(e);
}

/** Parsed values and, per concept, what is wrong with the entry. */
export function parseEntries(
  concepts: ConceptDef[],
  entries: Entries,
): { values: FormValues; errors: Record<string, string> } {
  const values: FormValues = {};
  const errors: Record<string, string> = {};
  for (const c of concepts) {
    if (c.calc) continue;
    const v = parseEntry(c, entries[c.code]);
    if (v === null) continue;
    const problem = checkValue(c, v);
    if (problem) errors[c.code] = problem;
    else values[c.code] = v;
  }
  return { values, errors };
}

const fmt = (v: Value | undefined) => (v === null || v === undefined ? '' : String(v));

export function ConceptInput(props: {
  concept: ConceptDef;
  label?: string | undefined;
  help?: string | undefined;
  entry: Entry | undefined;
  calculated?: Value | undefined;
  error?: string | undefined;
  onChange: (e: Entry | undefined) => void;
}) {
  const c = props.concept;
  const id = `f-${c.code}`;
  const label = props.label ?? c.display;
  const help = props.help ?? c.help;
  // the label may already name the unit, as on the screens it was ported from ("Temperature °C")
  const unit = c.unit && !label.includes(c.unit) ? <span class="unit">{c.unit}</span> : null;
  let control: preact.JSX.Element;
  if (c.calc) {
    control = (
      <div class="calc" id={id}>
        {fmt(props.calculated) || '—'} {unit} <span class="muted">({t.calculated})</span>
      </div>
    );
  } else if (c.type === 'boolean') {
    const v = props.entry;
    const pick = (x: boolean) => props.onChange(v === x ? undefined : x);
    control = (
      <fieldset class="tri" aria-labelledby={`${id}-l`}>
        <button
          type="button"
          class={v === true ? 'on yes' : ''}
          aria-pressed={v === true}
          onClick={() => pick(true)}
        >
          {t.yes}
        </button>
        <button
          type="button"
          class={v === false ? 'on no' : ''}
          aria-pressed={v === false}
          onClick={() => pick(false)}
        >
          {t.no}
        </button>
      </fieldset>
    );
  } else if (c.search && c.choices?.length) {
    control = (
      <SearchPicker
        id={id}
        concept={c}
        entry={typeof props.entry === 'string' ? props.entry : undefined}
        onChange={props.onChange}
      />
    );
  } else if (c.type === 'multi' && c.choices?.length) {
    const picked = new Set(
      (typeof props.entry === 'string' ? props.entry : '').split(',').filter(Boolean),
    );
    const toggle = (code: string) => {
      const next = new Set(picked);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      const list = (c.choices ?? []).map((x) => String(x.code)).filter((x) => next.has(x));
      props.onChange(list.length ? list.join(',') : undefined);
    };
    control = (
      <fieldset class="tri wrapbtn" aria-labelledby={`${id}-l`}>
        {c.choices.map((ch) => (
          <button
            key={ch.code}
            type="button"
            class={picked.has(String(ch.code)) ? 'on' : ''}
            aria-pressed={picked.has(String(ch.code))}
            onClick={() => toggle(String(ch.code))}
          >
            {ch.display}
          </button>
        ))}
      </fieldset>
    );
  } else if (c.choices?.length) {
    const v = props.entry === undefined ? '' : String(props.entry);
    control = (
      <fieldset class="tri wrapbtn" aria-labelledby={`${id}-l`}>
        {c.choices.map((ch) => (
          <button
            key={ch.code}
            type="button"
            class={v === String(ch.code) ? 'on' : ''}
            aria-pressed={v === String(ch.code)}
            onClick={() => props.onChange(v === String(ch.code) ? undefined : String(ch.code))}
          >
            {ch.display}
          </button>
        ))}
      </fieldset>
    );
  } else if (c.type === 'datetime') {
    // Typed as clinic time; stored as a FHIR dateTime with the clinic offset.
    const nowIso = clock.now();
    const v = typeof props.entry === 'string' ? props.entry.slice(0, 16) : '';
    control = (
      <input
        id={id}
        type="datetime-local"
        class="dt"
        max={nowIso.slice(0, 16)}
        value={v}
        onInput={(e) => {
          const x = (e.target as HTMLInputElement).value;
          props.onChange(x ? `${x.slice(0, 16)}:00${nowIso.slice(-6)}` : undefined);
        }}
      />
    );
  } else if (c.type === 'quantity' || c.type === 'integer') {
    control = (
      <div class="num">
        <input
          id={id}
          inputMode={c.type === 'integer' ? 'numeric' : 'decimal'}
          autocomplete="off"
          value={typeof props.entry === 'string' ? props.entry : ''}
          onInput={(e) => props.onChange((e.target as HTMLInputElement).value || undefined)}
        />
        {unit}
      </div>
    );
  } else if (c.long) {
    control = (
      <textarea
        id={id}
        rows={4}
        maxLength={2000}
        value={typeof props.entry === 'string' ? props.entry : ''}
        onInput={(e) => props.onChange((e.target as HTMLTextAreaElement).value || undefined)}
      />
    );
  } else {
    control = (
      <input
        id={id}
        value={typeof props.entry === 'string' ? props.entry : ''}
        onInput={(e) => props.onChange((e.target as HTMLInputElement).value || undefined)}
      />
    );
  }
  return (
    <div class={props.error ? 'field bad' : 'field'}>
      {c.type === 'boolean' || (c.choices?.length && !c.search) || c.calc ? (
        <span class="label" id={`${id}-l`}>
          {label}
        </span>
      ) : (
        <label id={`${id}-l`} for={id}>
          {label}
        </label>
      )}
      {control}
      {help ? <div class="help">{help}</div> : null}
      {props.error ? <div class="err">{props.error}</div> : null}
    </div>
  );
}
